import { createContext, useContext, useEffect, useState } from "react";

const translations = {
  en: {
    nav: { about: "About", tracks: "Tracks", lab: "The Lab", cta: "Partner With Us" },
    hero: {
      eyebrow: "Tema, Ghana — Abidjan, Côte d'Ivoire",
      lines: ["We build", "digital labs", "for every school."],
      accentIndex: 1,
      subtitle:
        "The Geeks Labs makes ICT an integral part of the school program — setting up computer labs and delivering hands-on IT training for students across West Africa.",
      cta1: "Explore Tracks",
      cta2: "Start A Project",
      scroll: "Scroll",
    },
    marquee: {
      items: [
        "Computer Labs",
        "Cyber Security",
        "Programming",
        "Certification",
        "ICT For Every School",
        "IT Infrastructure",
        "Youth Employment",
      ],
    },
    about: {
      eyebrow: "About Us — Who We Are",
      chapters: [
        {
          title: "Teachers First",
          body: "We're here to make teachers and educators' lives easier. Technology is becoming an integral part of life on the African continent, as in the rest of the world — and schools are increasingly dependent on it. Administrators, staff and teachers already deal with enough pressure. The Geeks Labs takes the IT support headaches — setting up, maintaining and time-consuming hardware updates — off their shoulders.",
        },
        {
          title: "Good Is Not Enough",
          body: "As part of the DAAKYI group, a leading technology company, we deliver products and services that help schools and communities work smarter, not harder. Our work never stops at \u201cgood enough.\u201d",
        },
        {
          title: "Your Students, Our Technology",
          body: "Focus on your students and let us focus on your technology. From a rapidly changing learning environment, to equipping students with job-ready skills, to securing your investment in technology — we are here to take the burden off your shoulders.",
        },
      ],
      hiringA: "We're",
      hiringB: "Hiring",
      hiringBody:
        "We're always looking for exceptional people to join The Geeks Labs and Daakyi. Do you have the drive to help schools and communities thrive with the right technology? Check out our current openings or drop us a message to express your interest.",
      hiringCta: "Drop Us A Message",
    },
    services: {
      eyebrow: "What We Teach",
      headingA: "Six Training",
      headingB: "Tracks",
      side: "Headphones, smart boards, projectors — every lab ships with the tools that make learning stick.",
      tracks: [
        { title: "IT Infrastructure", desc: "Build, sustain and manage technical infrastructure — networks, servers and the hardware that powers modern schools." },
        { title: "Programming & Databases", desc: "Programming languages, database construction and management — the core craft of software." },
        { title: "Enterprise Applications", desc: "ERP, CRM, call-center and billing systems — the software that runs organizations." },
        { title: "Desktop Applications", desc: "Everyday programs and productivity tools, mastered by every desktop user." },
        { title: "Certification & Bootcamps", desc: "Certifications, compliance, exam prep and intensive bootcamp-style training." },
        { title: "Cyber Security", desc: "IT network and system security — training the defenders of West Africa's digital future." },
      ],
    },
    lab: {
      eyebrow: "The Lab Program",
      headingA: "A Computer Lab In",
      headingB: "Every School",
      body: "ICT illiteracy and lack of access hold back brilliant students. We close that gap — designing, equipping and staffing computer labs that double as research and learning centers, from Tema to Abidjan.",
      stats: [
        "Training Tracks",
        "Countries — Ghana & Côte d'Ivoire",
        "Mission — ICT For Every School",
      ],
    },
    contact: {
      eyebrow: "Contact",
      headingA: "Start A",
      headingB: "Conversation",
      intro: "A school that needs a lab, a partner, a sponsor, or a future geek — every message lands directly with our team.",
      firstName: "First Name *",
      lastName: "Last Name *",
      email: "Email *",
      phone: "Phone Number",
      topicLabel: "How Can We Help You?",
      topicPlaceholder: "Select…",
      topics: ["Abidjan office", "Tema office", "General tech support"],
      message: "Message *",
      firstPh: "First name",
      lastPh: "Last name",
      emailPh: "you@school.edu.gh",
      phonePh: "+233 ...",
      msgPh: "Tell us about your school or project…",
      submit: "Send Enquiry",
      sending: "Sending…",
      success: "Enquiry received — our team will reach out shortly.",
      error: "Could not send your enquiry. Please try WhatsApp.",
      waGhana: "WhatsApp Ghana",
      waAbidjan: "WhatsApp Abidjan",
      ghanaLabel: "Ghana",
      ciLabel: "Côte d'Ivoire",
    },
    footer: {
      tagline: "Building lifelong relationships. Creating value. Excellence — in every lab, in every school.",
      reachUs: "Reach Us",
      rights: "All rights reserved.",
      top: "Back To Top",
    },
  },
  fr: {
    nav: { about: "À Propos", tracks: "Formations", lab: "Le Labo", cta: "Devenir Partenaire" },
    hero: {
      eyebrow: "Tema, Ghana — Abidjan, Côte d'Ivoire",
      lines: ["Nous créons", "des labos numériques", "pour chaque école."],
      accentIndex: 1,
      subtitle:
        "The Geeks Labs intègre les TIC au programme scolaire — installation de salles informatiques et formation pratique aux technologies pour les élèves d'Afrique de l'Ouest.",
      cta1: "Découvrir Les Formations",
      cta2: "Démarrer Un Projet",
      scroll: "Défiler",
    },
    marquee: {
      items: [
        "Salles Informatiques",
        "Cybersécurité",
        "Programmation",
        "Certification",
        "Les TIC Pour Chaque École",
        "Infrastructure IT",
        "Emploi Des Jeunes",
      ],
    },
    about: {
      eyebrow: "À Propos — Qui Sommes-Nous",
      chapters: [
        {
          title: "Les Enseignants D'Abord",
          body: "Nous sommes là pour simplifier la vie des enseignants et des éducateurs. La technologie devient partie intégrante de la vie sur le continent africain, comme dans le reste du monde — et les écoles en dépendent de plus en plus. Les administrateurs, le personnel et les enseignants subissent déjà assez de pression. The Geeks Labs leur enlève les soucis informatiques — installation, maintenance et mises à jour matérielles chronophages.",
        },
        {
          title: "Bien Ne Suffit Pas",
          body: "En tant que membre du groupe DAAKYI, une entreprise technologique de premier plan, nous fournissons des produits et services qui aident les écoles et les communautés à travailler plus intelligemment, pas plus durement. Notre travail ne s'arrête jamais à « assez bien ».",
        },
        {
          title: "Vos Élèves, Notre Technologie",
          body: "Concentrez-vous sur vos élèves et laissez-nous nous occuper de votre technologie. D'un environnement d'apprentissage en pleine évolution à l'acquisition de compétences professionnelles par les élèves, en passant par la protection de votre investissement technologique — nous sommes là pour vous soulager.",
        },
      ],
      hiringA: "Nous",
      hiringB: "Recrutons",
      hiringBody:
        "Nous recherchons toujours des personnes exceptionnelles pour rejoindre The Geeks Labs et Daakyi. Avez-vous la volonté d'aider les écoles et les communautés à prospérer grâce à la bonne technologie ? Consultez nos offres actuelles ou envoyez-nous un message pour exprimer votre intérêt.",
      hiringCta: "Écrivez-Nous",
    },
    services: {
      eyebrow: "Ce Que Nous Enseignons",
      headingA: "Six Parcours De",
      headingB: "Formation",
      side: "Casques, tableaux intelligents, projecteurs — chaque salle est livrée avec les outils qui font que l'apprentissage reste.",
      tracks: [
        { title: "Infrastructure IT", desc: "Construire, maintenir et gérer l'infrastructure technique — réseaux, serveurs et le matériel qui alimente les écoles modernes." },
        { title: "Programmation & Bases De Données", desc: "Langages de programmation, construction et gestion de bases de données — le cœur du métier du logiciel." },
        { title: "Applications D'Entreprise", desc: "ERP, CRM, centres d'appels et systèmes de facturation — les logiciels qui font tourner les organisations." },
        { title: "Applications Bureautiques", desc: "Les programmes et outils de productivité du quotidien, maîtrisés par chaque utilisateur." },
        { title: "Certification & Bootcamps", desc: "Certifications, conformité, préparation aux examens et formations intensives de type bootcamp." },
        { title: "Cybersécurité", desc: "Sécurité des réseaux et systèmes informatiques — former les défenseurs de l'avenir numérique de l'Afrique de l'Ouest." },
      ],
    },
    lab: {
      eyebrow: "Le Programme Labo",
      headingA: "Une Salle Informatique Dans",
      headingB: "Chaque École",
      body: "L'illettrisme numérique et le manque d'accès freinent des élèves brillants. Nous comblons ce fossé — conception, équipement et encadrement de salles informatiques qui servent aussi de centres de recherche et d'apprentissage, de Tema à Abidjan.",
      stats: [
        "Parcours De Formation",
        "Pays — Ghana & Côte d'Ivoire",
        "Mission — Les TIC Pour Chaque École",
      ],
    },
    contact: {
      eyebrow: "Contact",
      headingA: "Lancer La",
      headingB: "Conversation",
      intro: "Une école qui a besoin d'une salle informatique, un partenaire, un sponsor ou un futur geek — chaque message arrive directement à notre équipe.",
      firstName: "Prénom *",
      lastName: "Nom *",
      email: "E-mail *",
      phone: "Numéro De Téléphone",
      topicLabel: "Comment Pouvons-Nous Vous Aider ?",
      topicPlaceholder: "Sélectionner…",
      topics: ["Bureau d'Abidjan", "Bureau de Tema", "Support technique général"],
      message: "Message *",
      firstPh: "Prénom",
      lastPh: "Nom",
      emailPh: "vous@ecole.ci",
      phonePh: "+225 ...",
      msgPh: "Parlez-nous de votre école ou de votre projet…",
      submit: "Envoyer",
      sending: "Envoi…",
      success: "Message reçu — notre équipe vous contactera bientôt.",
      error: "Impossible d'envoyer votre message. Essayez WhatsApp.",
      waGhana: "WhatsApp Ghana",
      waAbidjan: "WhatsApp Abidjan",
      ghanaLabel: "Ghana",
      ciLabel: "Côte d'Ivoire",
    },
    footer: {
      tagline: "Des relations durables. De la valeur. L'excellence — dans chaque salle, dans chaque école.",
      reachUs: "Contactez-Nous",
      rights: "Tous droits réservés.",
      top: "Haut De Page",
    },
  },
};

const LangContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem("gl_lang");
      if (saved) return saved;
    } catch {}
    try {
      return (navigator.language || "").toLowerCase().startsWith("fr") ? "fr" : "en";
    } catch {
      return "en";
    }
  });

  const setLang = (l) => {
    try {
      localStorage.setItem("gl_lang", l);
    } catch {}
    setLangState(l);
  };

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = (key) => {
    const [ns, ...rest] = key.split(".");
    const path = rest.join(".");
    const from = translations[lang]?.[ns];
    const fallback = translations.en[ns];
    if (!path) return from ?? fallback ?? key;
    const val = path.split(".").reduce((o, p) => (o == null ? o : o[p]), from);
    if (val != null) return val;
    return path.split(".").reduce((o, p) => (o == null ? o : o[p]), fallback) ?? key;
  };

  return <LangContext.Provider value={{ lang, setLang, t }}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);
